import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus, Check, ArrowLeft, Search, ChevronDown, Dumbbell, History, ChevronUp, GripVertical, Download, Info, X, Timer, Trophy, Zap, Flame, MoreVertical } from "lucide-react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AddCustomExerciseModal } from "@/components/AddCustomExerciseModal";
import { WorkoutCompletionModal } from "@/components/WorkoutCompletionModal";
import { ShareWorkoutModal } from "@/components/ShareWorkoutModal";
import { ShareableWorkout } from "@/components/ShareWorkoutCard";
import { IncompleteWorkoutModal } from "@/components/IncompleteWorkoutModal";
import { ExerciseHistoryModal } from "@/components/ExerciseHistoryModal";
import { ExerciseInfoModal } from "@/components/ExerciseInfoModal";
import { WorkoutTimer } from "@/components/WorkoutTimer";
import { RestTimer } from "@/components/RestTimer";
import { WorkoutProgramImportModal } from "@/components/WorkoutProgramImportModal";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { EXERCISES, Exercise } from "@/data/exercises";
import { WorkoutProgramDay } from "@/hooks/useWorkoutPrograms";
import { Analytics } from "@/lib/analytics";
import { CHAT_DEFER_CONTEXT_REFRESH_KEY } from "@/lib/runningCoachPlans";
import { Capacitor } from "@capacitor/core";
import { syncGymPlanToWatch, startPhoneGymSession, signalWatchGymEnd } from "@/lib/straivWatch";
import { useUnitFormatters } from "@/hooks/useUnits";
import { kgToDisplayWeight, displayWeightToKg } from "@/lib/units";
import { usePremium } from "@/hooks/usePremium";
import {
  isProgramDayRunning,
  prepareProgramRunLaunch,
  toastOpeningRun,
} from "@/lib/startProgramRun";

interface WorkoutSet {
  id: string;
  reps: number | null;
  weight: number | null;
  completed: boolean;
  rest_seconds_taken?: number;
  isDropSet?: boolean;
  isWeightPB?: boolean;
  isRepsPB?: boolean;
  isTotalWeightPB?: boolean;
}

interface WorkoutExercise {
  exercise: Exercise | CustomExercise;
  sets: WorkoutSet[];
  durationMinutes?: number;
  notes?: string;
}

interface CustomExercise {
  id: string;
  name: string;
  description?: string;
  category: string;
  muscle_groups: string[];
  equipment?: string;
  instructions?: string;
  is_custom?: boolean;
}

interface RecentExercise {
  exercise_name: string;
  exercise_type: 'built-in' | 'custom';
  custom_exercise_id?: string;
  last_used_at: string;
  use_count: number;
}

const CARDIO_NAMES = ['treadmill','elliptical','burpees','burpee','jump rope','cycling','rowing machine','stair climber','stationary bike','spin bike','air bike','assault bike','ski erg','ski-erg','skierg','bike','rower'];

// Detect Android platform
const isAndroidPlatform = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);

// Handle focus - select all text programmatically
// On Android, we use setSelectionRange which doesn't trigger the white overlay
// because CSS user-select:none prevents the native selection UI
const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
  const input = e.target;
  
  // Select all text after a brief delay to ensure focus is complete
  setTimeout(() => {
    if (document.activeElement === input && input.value.length > 0) {
      // setSelectionRange selects text without triggering Android's action mode
      input.setSelectionRange(0, input.value.length);
    }
  }, 50);
  
  // Scroll input into view to prevent keyboard overlap (Android & iOS)
  setTimeout(() => {
    if (document.activeElement === input) {
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 300);
};

// Handle Enter key - dismiss keyboard
const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }
};

const isCardioExercise = (ex: Exercise | CustomExercise) => {
  const name = ex.name.toLowerCase();
  const byName = CARDIO_NAMES.some(n => name.includes(n));
  const byCategory = ex.category && ex.category.toLowerCase() === 'cardio';
  return byName || byCategory;
};

// Check if exercise is time-based (like Plank, Hollow Body Hold, etc.)
const isTimeBasedExercise = (ex: Exercise | CustomExercise) => {
  // Check if exercise has isTimeBased flag from the exercise database
  if ('isTimeBased' in ex && ex.isTimeBased) {
    return true;
  }
  return false;
};

export const GymWorkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { units, weightLabel, formatWeight } = useUnitFormatters();
  const { isPremium } = usePremium();
  const [workoutTitle, setWorkoutTitle] = useState("");
  const [workoutExercises, setWorkoutExercises] = useState<WorkoutExercise[]>([]);
  const [exerciseSearch, setExerciseSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [showExerciseList, setShowExerciseList] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [workoutStarted, setWorkoutStarted] = useState(false);
  const [workoutStartTime, setWorkoutStartTime] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedExerciseForHistory, setSelectedExerciseForHistory] = useState<string>("");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedExerciseForInfo, setSelectedExerciseForInfo] = useState<string>("");
  const [customExercises, setCustomExercises] = useState<CustomExercise[]>([]);
  const [recentExercises, setRecentExercises] = useState<RecentExercise[]>([]);
  const [showCustomExerciseModal, setShowCustomExerciseModal] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [completedWorkoutData, setCompletedWorkoutData] = useState<{
    title: string;
    duration: number;
    completedSets: number;
    workoutDetails: any;
    workoutId?: string;
  } | null>(null);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [incompleteExercises, setIncompleteExercises] = useState<Array<{
    name: string;
    incompleteSets: number;
    totalSets: number;
  }>>([]);
  const [showProgramImportModal, setShowProgramImportModal] = useState(false);
  const [weightInputs, setWeightInputs] = useState<Record<string, string>>({});
  const [repsInputs, setRepsInputs] = useState<Record<string, string>>({});
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [lastExerciseData, setLastExerciseData] = useState<Record<string, any>>({});
  const [activeRestTimer, setActiveRestTimer] = useState<{ exerciseIndex: number; setIndex: number } | null>(null);
  
  // PB tracking state
  type AllTimeBestCache = {
    maxWeight: number;
    maxReps: number;
    maxRepsAtWeight: Record<number, number>;
    maxTotalWeight: number;
    noPriorAttempts?: boolean;
  };
  const [allTimeBests, setAllTimeBests] = useState<Record<string, AllTimeBestCache>>({});
  const [sessionBests, setSessionBests] = useState<Record<string, { maxWeight: number; maxReps: number; maxRepsAtWeight: Record<number, number>; maxTotalWeight: number }>>({});

  // Combine built-in exercises with custom exercises
  const allExercises = [
    ...EXERCISES,
    ...customExercises.map(ex => ({ ...ex, is_custom: true }))
  ];

  // Get unique categories from all exercises
  const categories = ["All", ...Array.from(new Set(allExercises.map(exercise => exercise.category)))].sort();

  // Sort exercises: recent exercises first, then alphabetically
  const sortedExercises = [...allExercises].sort((a, b) => {
    const aIsCustom = 'is_custom' in a && a.is_custom;
    const bIsCustom = 'is_custom' in b && b.is_custom;
    
    const aRecent = recentExercises.find(r => 
      r.exercise_name === a.name && 
      ((r.exercise_type === 'custom' && aIsCustom) || (r.exercise_type === 'built-in' && !aIsCustom))
    );
    const bRecent = recentExercises.find(r => 
      r.exercise_name === b.name && 
      ((r.exercise_type === 'custom' && bIsCustom) || (r.exercise_type === 'built-in' && !bIsCustom))
    );
    
    if (aRecent && !bRecent) return -1;
    if (!aRecent && bRecent) return 1;
    if (aRecent && bRecent) {
      return new Date(bRecent.last_used_at).getTime() - new Date(aRecent.last_used_at).getTime();
    }
    return a.name.localeCompare(b.name);
  });

  // Filter exercises based on search and category (normalize hyphens so
  // "close grip" matches "Close-Grip Lat Pulldown").
  const filteredExercises = (() => {
    const norm = (s: string) =>
      s.toLowerCase().replace(/[-_/]+/g, ' ').replace(/\s+/g, ' ').trim();
    const q = norm(exerciseSearch);
    const tokens = q ? q.split(' ').filter(Boolean) : [];

    const matched = sortedExercises.filter((exercise) => {
      const hay = [
        norm(exercise.name),
        ...exercise.muscle_groups.map(norm),
        norm(exercise.equipment || ''),
      ].join(' ');
      const matchesSearch =
        !q ||
        hay.includes(q) ||
        (tokens.length > 0 && tokens.every((t) => hay.includes(t)));
      const matchesCategory = selectedCategory === "All" || exercise.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    if (!q) return matched;

    // Prefer name starts-with / token hits over recent-only ordering.
    const score = (exercise: (typeof matched)[number]) => {
      const name = norm(exercise.name);
      if (name.startsWith(q)) return 0;
      if (tokens.length && tokens.every((t) => name.includes(t))) return 1;
      if (name.includes(q)) return 2;
      return 3;
    };
    return [...matched].sort((a, b) => {
      const d = score(a) - score(b);
      return d !== 0 ? d : a.name.localeCompare(b.name);
    });
  })();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Don't close dropdown if clicking inside a modal/dialog
      if (target.closest('[role="dialog"]') || target.closest('[data-radix-portal]')) {
        return;
      }
      if (!target.closest('.exercise-search-container')) {
        setShowExerciseList(false);
      }
      if (!target.closest('.category-dropdown-container')) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load custom exercises and recent exercises
  useEffect(() => {
    const loadExerciseData = async () => {
      if (!user) return;

      try {
        // Load custom exercises
        const { data: customExercisesData, error: customError } = await supabase
          .from('custom_exercises')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (customError) throw customError;
        setCustomExercises(customExercisesData || []);

        // Load recent exercises
        const { data: recentData, error: recentError } = await supabase
          .from('recent_exercises')
          .select('*')
          .eq('user_id', user.id)
          .order('last_used_at', { ascending: false })
          .limit(20);

        if (recentError) throw recentError;
        
        const typedRecentData: RecentExercise[] = (recentData || []).map(item => ({
          exercise_name: item.exercise_name,
          exercise_type: item.exercise_type as 'built-in' | 'custom',
          custom_exercise_id: item.custom_exercise_id,
          last_used_at: item.last_used_at,
          use_count: item.use_count
        }));
        
        setRecentExercises(typedRecentData);

      } catch (error) {
        console.error('Error loading exercise data:', error);
      }
    };

    loadExerciseData();
  }, [user]);

  // Push today's gym plan to Apple Watch when exercises are loaded on iPhone.
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
    if (workoutExercises.length === 0) return;
    const plan = {
      title: workoutTitle || 'Gym Workout',
      exercises: workoutExercises.map((we, index) => {
        const ex = we.exercise;
        const targetReps = we.sets.find((s) => s.reps != null)?.reps ?? 10;
        return {
          id: 'id' in ex && ex.id ? String(ex.id) : `ex-${index}`,
          name: ex.name,
          targetSets: Math.max(1, we.sets.length),
          targetReps: Math.max(1, targetReps),
          restSeconds: 90,
        };
      }),
    };
    void syncGymPlanToWatch(plan);
    Analytics.gymPlanSyncedToWatch(plan.exercises?.length);
  }, [workoutExercises, workoutTitle]);

  // Launch Straiv Watch gym session when the user starts a workout on iPhone.
  useEffect(() => {
    if (!workoutStarted) return;
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
    void startPhoneGymSession().then(() => {
      Analytics.watchGymSessionStarted();
    }).catch(() => undefined);
    return () => {
      void signalWatchGymEnd().then(() => {
        Analytics.watchGymSessionEnded();
      }).catch(() => undefined);
    };
  }, [workoutStarted]);

  // Save workout session to localStorage whenever workout state changes
  useEffect(() => {
    if (workoutStarted && workoutExercises.length > 0 && workoutStartTime) {
      const currentWorkoutId = searchParams.get('workoutId');
      const workoutSession = {
        workoutId: currentWorkoutId || null,
        workoutTitle,
        workoutExercises,
        workoutStartTime: workoutStartTime.toISOString(),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('activeWorkoutSession', JSON.stringify(workoutSession));
    }
  }, [workoutStarted, workoutExercises, workoutTitle, workoutStartTime]);

  // Additional save mechanism to ensure data is always saved when modified
  const saveWorkoutSession = () => {
    if (workoutStarted && workoutExercises.length > 0 && workoutStartTime) {
      const currentWorkoutId = searchParams.get('workoutId');
      const workoutSession = {
        workoutId: currentWorkoutId || null,
        workoutTitle,
        workoutExercises,
        workoutStartTime: workoutStartTime.toISOString(),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem('activeWorkoutSession', JSON.stringify(workoutSession));
    }
  };

  // Load saved workout session on component mount
  useEffect(() => {
    const savedSession = localStorage.getItem('activeWorkoutSession');
    const pendingImport = localStorage.getItem('importedWorkout');
    // If there's a pending import, skip restoring a previous session to avoid overwriting imported values
    if (pendingImport) return;
    if (savedSession && !workoutStarted && workoutExercises.length === 0) {
      try {
        const session = JSON.parse(savedSession);
        // Only restore if the session is from the same day (prevent stale sessions)
        const sessionDate = new Date(session.timestamp);
        const now = new Date();
        const hoursDiff = Math.abs(now.getTime() - sessionDate.getTime()) / (1000 * 60 * 60);
        
        if (hoursDiff < 12) { // Only restore sessions less than 12 hours old
          const currentWorkoutId = searchParams.get('workoutId');
          if (!currentWorkoutId || !session.workoutId || session.workoutId === currentWorkoutId) {
            setWorkoutTitle(session.workoutTitle || '');
            setWorkoutExercises(session.workoutExercises || []);
            setWorkoutStartTime(new Date(session.workoutStartTime));
            setWorkoutStarted(true);
            Analytics.workoutStarted(session.workoutTitle || 'Resumed Workout');
          }
        } else {
          localStorage.removeItem('activeWorkoutSession');
        }
      } catch (error) {
        console.error('Error loading saved workout session:', error);
        localStorage.removeItem('activeWorkoutSession');
      }
    }
  }, []);

  const exerciseNamesKey = useMemo(
    () =>
      workoutExercises
        .filter((ex) => !isCardioExercise(ex.exercise))
        .map((ex) => ex.exercise.name.trim().toLowerCase())
        .join('|'),
    [workoutExercises],
  );

  // Fetch last exercise data for all current exercises (keyed by exercise name) — one join query each.
  useEffect(() => {
    if (!user || workoutExercises.length === 0) return;

    const fetchLastExerciseData = async () => {
      const updates: Record<string, any> = {};
      const pending = workoutExercises.filter((exercise) => {
        if (isCardioExercise(exercise.exercise)) return false;
        const nameKey = exercise.exercise.name.trim().toLowerCase();
        return !lastExerciseData[nameKey];
      });

      await Promise.all(
        pending.map(async (exercise) => {
          const nameKey = exercise.exercise.name.trim().toLowerCase();
          try {
            // Fetch several matches and pick latest by completed_at client-side.
            // PostgREST .order(referencedTable)+.limit(1) does NOT return the latest parent row.
            const { data: rows, error: workoutError } = await supabase
              .from('exercises')
              .select('workout_id, set_details, weight_kg, reps, sets, workouts!inner(completed_at)')
              .eq('workouts.user_id', user.id)
              .ilike('name', exercise.exercise.name)
              .not('workouts.completed_at', 'is', null)
              .limit(30);

            if (workoutError || !rows?.length) return;

            const withDates = rows
              .map((r: any) => {
                const w = Array.isArray(r.workouts) ? r.workouts[0] : r.workouts;
                return { ...r, completedAt: w?.completed_at as string | undefined };
              })
              .filter((r) => !!r.completedAt)
              .sort(
                (a, b) =>
                  new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime(),
              );

            const recentWorkout = withDates[0];
            if (!recentWorkout) return;

            let setDetails = Array.isArray(recentWorkout.set_details)
              ? recentWorkout.set_details
              : null;

            if (!setDetails || setDetails.length === 0) {
              // Legacy: one row per set — gather siblings from same workout.
              const siblings = withDates.filter((r) => r.workout_id === recentWorkout.workout_id);
              if (siblings.length > 1 || recentWorkout.reps || recentWorkout.weight_kg) {
                setDetails = siblings.map((r: any, i: number) => ({
                  set_number: i + 1,
                  reps: r.reps,
                  weight_kg: r.weight_kg,
                }));
              }
            }

            if (!setDetails || setDetails.length === 0) return;

            const usable = setDetails
              .filter((set: any) => set.reps || set.weight_kg || set.weight)
              .sort((a: any, b: any) => (a.set_number || 0) - (b.set_number || 0));

            if (usable.length > 0) {
              updates[nameKey] = usable;
            }
          } catch (error) {
            console.error(`Exception fetching data for ${exercise.exercise.name}:`, error);
          }
        }),
      );

      if (Object.keys(updates).length > 0) {
        setLastExerciseData((prev) => ({ ...prev, ...updates }));
      }
    };

    void fetchLastExerciseData();
  }, [user, exerciseNamesKey, workoutExercises.length]);

  // Fetch all-time bests for ALL exercises when workoutExercises changes
  // This ensures PB detection works for imported workouts (Arnold, programs, etc.)
  useEffect(() => {
    if (!user || workoutExercises.length === 0) return;

    const fetchAllBestsForExercises = async () => {
      const exercisesToFetch = workoutExercises
        .filter(ex => !isCardioExercise(ex.exercise) && !isTimeBasedExercise(ex.exercise))
        .filter(ex => !(ex.exercise.name in allTimeBests))
        .map(ex => ex.exercise.name);

      if (exercisesToFetch.length === 0) return;

      console.log('Fetching all-time bests for exercises:', exercisesToFetch);

      const updates: Record<string, AllTimeBestCache> = {};

      for (const exerciseName of exercisesToFetch) {
        const best = await fetchAllTimeBest(exerciseName);
        updates[exerciseName] = best ?? {
          maxWeight: 0,
          maxReps: 0,
          maxRepsAtWeight: {},
          maxTotalWeight: 0,
          noPriorAttempts: true,
        };
      }

      if (Object.keys(updates).length > 0) {
        console.log('Setting all-time bests for exercises:', Object.keys(updates));
        setAllTimeBests(prev => ({ ...prev, ...updates }));
      }
    };

    fetchAllBestsForExercises();
  }, [workoutExercises, user]);

  // Load imported workout from Arnold
  useEffect(() => {
    const importedWorkout = localStorage.getItem('importedWorkout');
    if (importedWorkout && !workoutStarted && workoutExercises.length === 0) {
      try {
        const workout = JSON.parse(importedWorkout);
        console.log('Imported workout from Arnold:', workout);
        
        if (workout.imported && workout.exercises) {
          setWorkoutTitle(workout.title || "AI Workout Plan");
          
          // Convert imported exercises to workout format
          const convertedExercises: WorkoutExercise[] = workout.exercises.map((importedEx: any) => {
            // Find the matching exercise from the database
            const matchedExercise = allExercises.find(ex => ex.name === importedEx.name);
            
            if (matchedExercise) {
              return {
                exercise: matchedExercise,
                sets: Array.from({ length: importedEx.sets || 3 }, () => ({
                  id: crypto.randomUUID(), // Use unique IDs to prevent cross-exercise state sharing
                  reps: importedEx.reps || null,
                  weight: importedEx.weight || null,
                  completed: false
                })),
                notes: importedEx.matched ? undefined : `Original: ${importedEx.name} (auto-matched to ${matchedExercise.name})`
              };
            } else {
              // Create a temporary custom exercise if no match found
              const tempCustomExercise: CustomExercise = {
                id: crypto.randomUUID(),
                name: importedEx.name,
                category: 'Other',
                muscle_groups: importedEx.muscleGroups || ['Full Body'],
                equipment: importedEx.equipment,
                is_custom: true
              };
              
              return {
                exercise: tempCustomExercise,
                sets: Array.from({ length: importedEx.sets || 3 }, () => ({
                  id: crypto.randomUUID(), // Use unique IDs to prevent cross-exercise state sharing
                  reps: importedEx.reps || null,
                  weight: importedEx.weight || null,
                  completed: false
                })),
                notes: "Exercise imported from AI workout plan"
              };
            }
          });
          
          setWorkoutExercises(convertedExercises);
          setWorkoutStartTime(new Date());
          setWorkoutStarted(true);
          Analytics.workoutStarted(workoutTitle || 'Imported Workout');
          toast.success(t('gymWorkout.toast.importedExercises', { count: convertedExercises.length }));
          
          // Clear the imported workout from localStorage
          localStorage.removeItem('importedWorkout');
        }
      } catch (error) {
        console.error('Error loading imported workout:', error);
        localStorage.removeItem('importedWorkout');
      }
    }
  }, [allExercises]); // Depend on allExercises to ensure they're loaded first

  // Warn user before leaving an active workout
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (workoutStarted && workoutExercises.length > 0) {
        e.preventDefault();
        e.returnValue = t('gymWorkout.beforeUnload');
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [workoutStarted, workoutExercises]);

  // Prefill from imported workout if workoutId is present
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const workoutId = searchParams.get('workoutId');
    if (!workoutId || !user) return;

    const loadPrefilled = async () => {
      try {
        const savedSession = localStorage.getItem('activeWorkoutSession');
        if (savedSession) {
          try {
            const session = JSON.parse(savedSession);
            const sessionDate = new Date(session.timestamp);
            const now = new Date();
            const hoursDiff = Math.abs(now.getTime() - sessionDate.getTime()) / (1000 * 60 * 60);
            const currentWorkoutId = searchParams.get('workoutId');
            if (hoursDiff < 12 && currentWorkoutId && session.workoutId === currentWorkoutId) {
              // Active session for this workout exists; skip prefill to avoid overwriting user data
              return;
            }
          } catch (e) {
            // If parsing fails, continue with prefill
          }
        }

        const { data, error } = await supabase
          .from('workouts')
          .select(`*, exercises(*)`)
          .eq('id', workoutId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;
        if (!data) return;

        setWorkoutTitle(data.title || 'Gym Workout');

        const mapped = (data.exercises || []).map((ex: any) => {
          const exObj: CustomExercise = {
            id: crypto.randomUUID(),
            name: ex.name,
            category: ex.category || 'Imported',
            muscle_groups: ex.muscle_groups || [],
            equipment: ex.equipment || undefined,
            is_custom: true,
          };

          // Treat cardio (duration-based) exercises differently
          if (ex.category === 'Cardio' || (ex.duration_seconds && ex.duration_seconds > 0)) {
            return {
              exercise: exObj,
              sets: [],
              durationMinutes: Math.round((ex.duration_seconds || 0) / 60)
            } as WorkoutExercise;
          }

          const setCount = Math.max(1, ex.sets || 3);
          const repsDefault = ex.reps || null;
          const weightDefault = ex.weight_kg ?? null;
          const sets: WorkoutSet[] = Array.from({ length: setCount }).map(() => ({
            id: crypto.randomUUID(),
            reps: repsDefault,
            weight: weightDefault,
            completed: false,
          }));

          return { exercise: exObj, sets };
        });

        if (mapped.length > 0) {
          setWorkoutExercises(mapped);
          setWorkoutStartTime(new Date());
          setWorkoutStarted(true);
          Analytics.workoutStarted(workoutTitle || 'Imported Workout');
        }
      } catch (e) {
        console.error('Error loading imported workout:', e);
      }
    };

    loadPrefilled();
  }, [searchParams, user]);

  const handleAnalyzeWorkout = () => {
    if (!completedWorkoutData) return;
    
    // Format workout data for Arnold - maintain exact exercise order
    const workoutSummary = {
      type: 'strength', // Explicitly set type for Chat.tsx handler
      title: completedWorkoutData.title,
      duration: completedWorkoutData.duration,
      completedSets: completedWorkoutData.completedSets,
      exercises: completedWorkoutData.workoutDetails.exercises.map((ex: any, index: number) => ({
        exerciseOrder: index + 1,
        name: ex.exercise.name,
        sets: ex.sets.filter((set: any) => set.completed).length,
        totalSets: ex.sets.length,
        muscleGroups: ex.exercise.muscle_groups,
        completedSets: ex.sets.filter((set: any) => set.completed).map((set: any) => ({
          reps: set.reps,
          weight: set.weight,
          restSecondsTaken: set.rest_seconds_taken || null,
          isDropSet: set.isDropSet || false,
          isWeightPB: set.isWeightPB || false,
          isRepsPB: set.isRepsPB || false,
          isTotalWeightPB: set.isTotalWeightPB || false
        })),
        notes: ex.notes || null
      })),
      _ts: Date.now() // Timestamp for freshness check
    };

    // Save to localStorage as fallback (in case navigation state is lost on Android)
    try {
      localStorage.setItem('pendingWorkoutAnalysis', JSON.stringify(workoutSummary));
      console.log('Saved workout analysis to localStorage:', workoutSummary);
    } catch (e) {
      console.warn('Failed to save workout analysis to localStorage:', e);
    }

    try {
      sessionStorage.setItem(CHAT_DEFER_CONTEXT_REFRESH_KEY, '1');
    } catch {
      /* ignore */
    }

    // Navigate to chat with workout data
    navigate("/chat", { 
      state: { 
        workoutData: workoutSummary,
        fromWorkout: true,
        message: `Hey! I just completed my workout "${completedWorkoutData.title}". Can you analyze my performance and give me feedback? Here are the details:`
      } 
    });
    setShowCompletionModal(false);
  };

  const handleAnalyzeLater = () => {
    setShowCompletionModal(false);
    navigate("/workouts");
  };

  const handleShareWorkout = async () => {
    if (!completedWorkoutData || !completedWorkoutData.workoutId) return;
    
    // Close completion modal and open share modal
    setShowCompletionModal(false);
    setShowShareModal(true);
  };

  // Build shareable workout data from completed workout
  const buildShareableWorkout = (): ShareableWorkout | null => {
    if (!completedWorkoutData) return null;
    
    const exercises = completedWorkoutData.workoutDetails?.exercises || [];
    const totalSets = exercises.reduce((sum: number, ex: WorkoutExercise) => 
      sum + ex.sets.filter((s: WorkoutSet) => s.completed).length, 0);
    const totalVolume = exercises.reduce((sum: number, ex: WorkoutExercise) => {
      return sum + ex.sets
        .filter((s: WorkoutSet) => s.completed)
        .reduce((setSum: number, s: WorkoutSet) => setSum + ((s.weight || 0) * (s.reps || 0)), 0);
    }, 0);
    
    return {
      id: completedWorkoutData.workoutId || 'unknown',
      title: completedWorkoutData.title,
      duration_minutes: completedWorkoutData.duration,
      completed_at: new Date().toISOString(),
      workout_type: 'strength',
      total_sets: totalSets,
      total_volume: totalVolume,
      exercises: exercises.map((ex: WorkoutExercise) => ({
        name: ex.exercise.name,
        sets: ex.sets.filter((s: WorkoutSet) => s.completed).length,
        reps: ex.sets.find((s: WorkoutSet) => s.completed)?.reps || undefined,
        weight_kg: ex.sets.find((s: WorkoutSet) => s.completed)?.weight || undefined,
      })),
    };
  };

  const addExercise = async (exercise: Exercise | CustomExercise) => {
    const cardio = isCardioExercise(exercise);
    const timeBased = isTimeBasedExercise(exercise);
    
    // Update UI immediately for instant feedback - defer async work
    let newWorkoutExercise: WorkoutExercise;
    if (cardio) {
      newWorkoutExercise = { exercise, sets: [], durationMinutes: 0 };
    } else if (timeBased) {
      // Time-based exercises use sets with duration tracking (in seconds)
      newWorkoutExercise = { exercise, sets: [{ id: crypto.randomUUID(), reps: null, weight: null, completed: false }] };
    } else {
      newWorkoutExercise = { exercise, sets: [{ id: crypto.randomUUID(), reps: null, weight: null, completed: false }] };
    }

    const newIndex = workoutExercises.length;
    setWorkoutExercises([...workoutExercises, newWorkoutExercise]);
    setExerciseSearch("");
    setShowExerciseList(false);
    if (!workoutStarted) {
      setWorkoutStartTime(new Date());
      setWorkoutStarted(true);
      Analytics.workoutStarted(workoutTitle || 'Untitled Workout');
    }
    Analytics.exerciseAdded(exercise.name, exercise.category);

    // Defer async work to background - don't block UI
    queueMicrotask(async () => {
      if (!cardio && !timeBased && user) {
        const best = await fetchAllTimeBest(exercise.name);
        setAllTimeBests(prev => ({
          ...prev,
          [exercise.name]: best ?? {
            maxWeight: 0,
            maxReps: 0,
            maxRepsAtWeight: {},
            maxTotalWeight: 0,
            noPriorAttempts: true,
          },
        }));
      }
    });

    // Fetch last exercise data if available (background)
    if (user && !cardio) {
      try {
        // First, find the most recent workout that has this exercise
        const { data: rows, error: workoutError } = await supabase
          .from('exercises')
          .select('workout_id, set_details, weight_kg, reps, sets, workouts!inner(completed_at)')
          .eq('workouts.user_id', user.id)
          .ilike('name', exercise.name)
          .not('workouts.completed_at', 'is', null)
          .limit(30);

        if (!workoutError && rows?.length) {
          const withDates = rows
            .map((r: any) => {
              const w = Array.isArray(r.workouts) ? r.workouts[0] : r.workouts;
              return { ...r, completedAt: w?.completed_at as string | undefined };
            })
            .filter((r) => !!r.completedAt)
            .sort(
              (a, b) =>
                new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime(),
            );
          const recentWorkout = withDates[0];
          let setDetails = Array.isArray(recentWorkout?.set_details)
            ? recentWorkout.set_details
            : null;

          if ((!setDetails || setDetails.length === 0) && recentWorkout) {
            const siblings = withDates.filter((r) => r.workout_id === recentWorkout.workout_id);
            if (siblings.length > 1 || recentWorkout.reps || recentWorkout.weight_kg) {
              setDetails = siblings.map((r: any, i: number) => ({
                set_number: i + 1,
                reps: r.reps,
                weight_kg: r.weight_kg,
              }));
            }
          }

          if (setDetails && setDetails.length > 0) {
            const usable = setDetails
              .filter((set: any) => set.reps || set.weight_kg || set.weight)
              .sort((a: any, b: any) => (a.set_number || 0) - (b.set_number || 0));

            if (usable.length > 0) {
              const nameKey = exercise.name.trim().toLowerCase();
              setLastExerciseData((prev) => ({
                ...prev,
                [nameKey]: usable,
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching last exercise data:', error);
      }
    }

    // Track recent exercise usage
    if (user) {
      try {
        const isCustom = 'is_custom' in exercise && exercise.is_custom;
        await supabase
          .from('recent_exercises')
          .upsert({
            user_id: user.id,
            exercise_name: exercise.name,
            exercise_type: isCustom ? 'custom' : 'built-in',
            custom_exercise_id: isCustom ? exercise.id : null,
            last_used_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,exercise_name,exercise_type,custom_exercise_id',
            ignoreDuplicates: false
          });
      } catch (error) {
        console.error('Error tracking recent exercise:', error);
        // Don't interrupt the workflow for this
      }
    }
  };

  const handleCustomExerciseAdded = (newExercise: CustomExercise) => {
    setCustomExercises([newExercise, ...customExercises]);
    // Automatically add the new custom exercise to the workout
    addExercise({ ...newExercise, is_custom: true });
  };

  const handleImportFromProgram = async (programDay: WorkoutProgramDay, programTitle: string) => {
    if (!programDay.exercises || programDay.exercises.length === 0) {
      toast.error(t('gymWorkout.toast.programDayEmpty'));
      return;
    }

    if (isProgramDayRunning(programDay.exercises)) {
      const result = await prepareProgramRunLaunch(programDay, { enableCoach: isPremium });
      if (!result?.planText) {
        toast.error(t('components.workoutProgramViewer.noRunningDetails', {
          defaultValue: 'No run plan found for this day',
        }));
        return;
      }
      toastOpeningRun(t('components.workoutProgramViewer.toast.openingRunning', {
        defaultValue: 'Opening AI Run Coach…',
      }));
      navigate('/running-start', {
        state: {
          openCoachGuide: true,
          coachGuidePlan: result.planText,
          requirePremiumForCoach: !isPremium,
          programTitle,
        },
      });
      return;
    }

    // Create workout exercises from program day
    // CRITICAL: Use crypto.randomUUID() for all IDs to prevent cross-exercise state sharing
    const importedExercises: WorkoutExercise[] = programDay.exercises.map((exercise) => {
      const customExercise: CustomExercise = {
        id: crypto.randomUUID(),
        name: exercise.exercise_name,
        category: 'Imported',
        muscle_groups: exercise.muscle_groups || [],
        equipment: undefined,
        is_custom: true,
      };

      const setCount = Math.max(1, exercise.sets || 3);
      const repsDefault = exercise.reps_min || null;
      const weightDefault = exercise.weight_kg || null;
      
      // Each set MUST have a unique ID to prevent input state pollution across exercises
      const sets: WorkoutSet[] = Array.from({ length: setCount }).map(() => ({
        id: crypto.randomUUID(),
        reps: repsDefault,
        weight: weightDefault,
        completed: false,
      }));

      return { exercise: customExercise, sets };
    });

    // Set workout title and exercises
    setWorkoutTitle(`${programTitle} - ${programDay.day_name || `Day ${programDay.day_number}`}`);
    setWorkoutExercises(importedExercises);
    
    if (!workoutStarted) {
      setWorkoutStartTime(new Date());
      setWorkoutStarted(true);
      Analytics.workoutStarted(workoutTitle || 'Program Workout');
    }

    toast.success(t('gymWorkout.toast.importedFromProgram', { count: importedExercises.length, day: programDay.day_name || `Day ${programDay.day_number}` }));
  };

  // Handle program import from Workouts page (navigated with state)
  const programImportHandled = useRef(false);
  useEffect(() => {
    if (programImportHandled.current) return;
    const state = location.state as { programDay?: WorkoutProgramDay; programTitle?: string } | null;
    if (state?.programDay && state?.programTitle) {
      programImportHandled.current = true;
      handleImportFromProgram(state.programDay, state.programTitle);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname]);

  const addSet = (exerciseIndex: number, copySameSet: boolean = false) => {
    const newSets = [...workoutExercises];
    
    if (copySameSet && newSets[exerciseIndex].sets.length > 0) {
      // Find the last completed set or the most recent set with data
      const lastCompletedSet = [...newSets[exerciseIndex].sets]
        .reverse()
        .find(set => set.completed && (set.reps !== null || set.weight !== null));
      
      const lastSetWithData = lastCompletedSet || [...newSets[exerciseIndex].sets]
        .reverse()
        .find(set => set.reps !== null || set.weight !== null);
      
      if (lastSetWithData) {
        newSets[exerciseIndex].sets.push({
          id: crypto.randomUUID(),
          reps: lastSetWithData.reps,
          weight: lastSetWithData.weight,
          completed: false
        });
      } else {
        // No previous data, add empty set
        newSets[exerciseIndex].sets.push({
          id: crypto.randomUUID(),
          reps: null,
          weight: null,
          completed: false
        });
      }
    } else {
      newSets[exerciseIndex].sets.push({
        id: crypto.randomUUID(),
        reps: null,
        weight: null,
        completed: false
      });
    }
    
    setWorkoutExercises(newSets);
    saveWorkoutSession();
  };

  const removeSet = (exerciseIndex: number, setIndex: number) => {
    const newSets = [...workoutExercises];
    if (newSets[exerciseIndex].sets.length > 1) {
      newSets[exerciseIndex].sets.splice(setIndex, 1);
      setWorkoutExercises(newSets);
    }
  };

  // Fetch all-time bests for an exercise (uses case-insensitive matching)
  const fetchAllTimeBest = async (exerciseName: string) => {
    if (!user || !exerciseName) return null;
    
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select(`
          name,
          reps,
          weight_kg,
          set_details,
          workouts!inner(
            completed_at,
            user_id
          )
        `)
        .eq('workouts.user_id', user.id)
        .ilike('name', exerciseName)
        .not('workouts.completed_at', 'is', null)
        .order('completed_at', { referencedTable: 'workouts', ascending: false });

      if (error) throw error;
      
      // No prior logged attempts — cannot earn a PB yet.
      if (!data || data.length === 0) {
        return null;
      }

      let maxWeight = 0;
      let maxReps = 0;
      let maxTotalWeight = 0;
      const maxRepsAtWeight: Record<number, number> = {};

      // Process all exercises to find bests
      data.forEach(exercise => {
        // Check set_details if available (new format)
        if (exercise.set_details && Array.isArray(exercise.set_details)) {
          exercise.set_details
            .filter((set: any) => set.completed !== false)
            .forEach((set: any) => {
            const weight = Number(set.weight_kg || set.weight || 0);
            const reps = Number(set.reps || 0);
            const totalWeight = weight * reps;
            
            if (weight > 0 && weight > maxWeight) {
              maxWeight = weight;
            }
            if (reps > 0 && reps > maxReps) {
              maxReps = reps;
            }
            if (totalWeight > 0 && totalWeight > maxTotalWeight) {
              maxTotalWeight = totalWeight;
            }
            if (weight > 0 && reps > 0) {
              const currentMax = maxRepsAtWeight[weight] || 0;
              if (reps > currentMax) {
                maxRepsAtWeight[weight] = reps;
              }
            }
          });
        } else {
          // Fallback to old format (single weight/reps per exercise)
          const weight = Number(exercise.weight_kg || 0);
          const reps = Number(exercise.reps || 0);
          const totalWeight = weight * reps;
          
          if (weight > 0 && weight > maxWeight) {
            maxWeight = weight;
          }
          if (reps > 0 && reps > maxReps) {
            maxReps = reps;
          }
          if (totalWeight > 0 && totalWeight > maxTotalWeight) {
            maxTotalWeight = totalWeight;
          }
          if (weight > 0 && reps > 0) {
            const currentMax = maxRepsAtWeight[weight] || 0;
            if (reps > currentMax) {
              maxRepsAtWeight[weight] = reps;
            }
          }
        }
      });

      return { maxWeight, maxReps, maxRepsAtWeight, maxTotalWeight };
    } catch (error) {
      console.error('Error fetching all-time best:', error);
      return null;
    }
  };

  // Check if a set is a PB and update state - ensures only one PB per type per exercise
  // This also syncs against current session's sets in real-time
  const checkAndUpdatePB = async (exerciseIndex: number, setIndex: number, weight: number | null, reps: number | null, currentSets: WorkoutExercise[]) => {
    const exercise = currentSets[exerciseIndex];
    if (!exercise || isCardioExercise(exercise.exercise) || isTimeBasedExercise(exercise.exercise)) return;

    const exerciseName = exercise.exercise.name;
    
    // CRITICAL: Ensure allTimeBests is loaded before checking PBs
    // If not loaded yet, fetch it now and wait
    let allTimeBest = allTimeBests[exerciseName] ?? null;
    if (allTimeBest === null && user) {
      // Fetch all-time bests synchronously before proceeding
      console.log(`[PB Check] Fetching all-time bests for ${exerciseName} before PB check`);
      const fetchedBest = await fetchAllTimeBest(exerciseName);
      if (fetchedBest) {
        allTimeBest = fetchedBest;
        setAllTimeBests(prev => ({ ...prev, [exerciseName]: fetchedBest }));
      } else {
        const noHistory: AllTimeBestCache = {
          maxWeight: 0,
          maxReps: 0,
          maxRepsAtWeight: {},
          maxTotalWeight: 0,
          noPriorAttempts: true,
        };
        allTimeBest = noHistory;
        setAllTimeBests(prev => ({ ...prev, [exerciseName]: noHistory }));
      }
    }

    // Create a deep copy of sets, preserving ALL current state (including completion status)
    const updatedSets = currentSets.map((ex, idx) => {
      if (idx === exerciseIndex) {
        return {
          ...ex,
          sets: ex.sets.map(set => ({
            ...set,
            // Clear ONLY PB flags for recalculation - keep completion status
            isWeightPB: false,
            isRepsPB: false,
            isTotalWeightPB: false
          }))
        };
      }
      return { ...ex, sets: ex.sets.map(s => ({ ...s })) };
    });

    // No prior attempts logged for this exercise — skip PB badges entirely.
    if (!allTimeBest || allTimeBest.noPriorAttempts) {
      setWorkoutExercises(updatedSets);
      return;
    }
    
    const sessionBest = sessionBests[exerciseName] || { maxWeight: 0, maxReps: 0, maxRepsAtWeight: {}, maxTotalWeight: 0 };

    // Find the actual bests across all sets in this exercise
    let maxWeightInExercise = 0;
    let maxRepsInExercise = 0;
    let maxTotalWeightInExercise = 0;
    let maxRepsAtWeightInExercise: Record<number, number> = {};

    updatedSets[exerciseIndex].sets.forEach(set => {
      if (set.weight !== null && set.weight > 0) {
        if (set.weight > maxWeightInExercise) {
          maxWeightInExercise = set.weight;
        }
        if (set.reps !== null && set.reps > 0) {
          const currentMax = maxRepsAtWeightInExercise[set.weight] || 0;
          if (set.reps > currentMax) {
            maxRepsAtWeightInExercise[set.weight] = set.reps;
          }
          // Calculate total weight (weight × reps)
          const totalWeight = set.weight * set.reps;
          if (totalWeight > maxTotalWeightInExercise) {
            maxTotalWeightInExercise = totalWeight;
          }
        }
      }
      if (set.reps !== null && set.reps > 0) {
        if (set.reps > maxRepsInExercise) {
          maxRepsInExercise = set.reps;
        }
      }
    });

    // IMPORTANT: PBs should be evaluated against ALL-TIME bests (not session bests)
    // Using session bests here would make PB badges disappear after subsequent edits.
    const baselineMaxWeight = allTimeBest.maxWeight;
    const baselineMaxReps = allTimeBest.maxReps;
    const baselineMaxTotalWeight = allTimeBest.maxTotalWeight;

    // Track which weights are new PBs (for avoiding false reps PBs at new weights)
    const newWeightPBs = new Set<number>();

    // Mark weight PB only when strictly beating a prior all-time record
    if (maxWeightInExercise > baselineMaxWeight && baselineMaxWeight > 0) {
      updatedSets[exerciseIndex].sets.forEach(set => {
        if (set.weight === maxWeightInExercise && set.weight > 0) {
          set.isWeightPB = true;
          newWeightPBs.add(set.weight);
        }
      });
    }

    // Mark reps PB at specific weights - but ONLY if there's actual all-time history at that weight
    // If the weight itself is a new weight PB, there can't be reps history at that weight.
    Object.keys(maxRepsAtWeightInExercise).forEach(weightStr => {
      const weight = parseFloat(weightStr);
      const repsAtWeight = maxRepsAtWeightInExercise[weight];

      // Skip if this weight is a new weight PB - can't have reps history at a new weight
      if (newWeightPBs.has(weight)) return;

      const allTimeMaxRepsAtWeight = (allTimeBest && allTimeBest.maxRepsAtWeight && allTimeBest.maxRepsAtWeight[weight] > 0)
        ? allTimeBest.maxRepsAtWeight[weight]
        : 0;

      // Only mark as reps PB if there's actual history at this weight AND we beat it
      // NO first-time marking for reps at specific weights - that would be too aggressive
      if (repsAtWeight > allTimeMaxRepsAtWeight && allTimeMaxRepsAtWeight > 0) {
        updatedSets[exerciseIndex].sets.forEach(set => {
          if (set.weight === weight && set.reps === repsAtWeight) {
            set.isRepsPB = true;
          }
        });
      }
    });

    // Also check for overall max reps PB (best reps regardless of weight)
    if (maxRepsInExercise > baselineMaxReps && baselineMaxReps > 0) {
      updatedSets[exerciseIndex].sets.forEach(set => {
        if (set.reps === maxRepsInExercise && set.reps > 0 && !set.isRepsPB) {
          set.isRepsPB = true;
        }
      });
    }

    // Mark total weight PB (weight × reps) — must strictly beat prior best
    if (maxTotalWeightInExercise > baselineMaxTotalWeight && baselineMaxTotalWeight > 0) {
      updatedSets[exerciseIndex].sets.forEach(set => {
        if (set.weight !== null && set.reps !== null && set.weight > 0 && set.reps > 0) {
          const totalWeight = set.weight * set.reps;
          if (totalWeight === maxTotalWeightInExercise) {
            set.isTotalWeightPB = true;
          }
        }
      });
    }


    // CRITICAL: Update state with PB flags - this ensures they persist
    // Directly use updatedSets which has all the correct PB flags and completion status
    // The currentSets parameter already has the latest completion status, and updatedSets preserves it
    setWorkoutExercises(updatedSets);

    // Update session bests
    const updatedSessionBest = { ...sessionBest };
    if (maxWeightInExercise > (updatedSessionBest.maxWeight || 0)) {
      updatedSessionBest.maxWeight = maxWeightInExercise;
    }
    if (maxRepsInExercise > (updatedSessionBest.maxReps || 0)) {
      updatedSessionBest.maxReps = maxRepsInExercise;
    }
    if (maxTotalWeightInExercise > (updatedSessionBest.maxTotalWeight || 0)) {
      updatedSessionBest.maxTotalWeight = maxTotalWeightInExercise;
    }
    Object.keys(maxRepsAtWeightInExercise).forEach(weightStr => {
      const weight = parseFloat(weightStr);
      const reps = maxRepsAtWeightInExercise[weight];
      const currentMax = updatedSessionBest.maxRepsAtWeight[weight] || 0;
      if (reps > currentMax) {
        updatedSessionBest.maxRepsAtWeight[weight] = reps;
      }
    });
    setSessionBests(prev => ({ ...prev, [exerciseName]: updatedSessionBest }));

    // Show celebration toast only for the set being updated
    const updatedSet = updatedSets[exerciseIndex].sets[setIndex];
    if (updatedSet.isWeightPB && updatedSet.isRepsPB) {
      toast.success(t('gymWorkout.toast.pbWeightAndReps', { weight: formatWeight(updatedSet.weight!), reps: updatedSet.reps }), { duration: 3000 });
    } else if (updatedSet.isWeightPB) {
      toast.success(t('gymWorkout.toast.pbWeightOnly', { weight: formatWeight(updatedSet.weight!) }), { duration: 3000 });
    } else if (updatedSet.isRepsPB) {
      toast.success(t('gymWorkout.toast.pbRepsOnly', { reps: updatedSet.reps }), { duration: 3000 });
    } else if (updatedSet.isTotalWeightPB) {
      // Show total weight PB only when no weight or reps PB (to avoid duplicate celebrations)
      const totalWeight = (updatedSet.weight || 0) * (updatedSet.reps || 0);
      toast.success(t('gymWorkout.toast.pbVolume', { total: formatWeight(totalWeight), weight: formatWeight(updatedSet.weight!), reps: updatedSet.reps }), { duration: 3000 });
    }
  };

  const updateSet = async (exerciseIndex: number, setIndex: number, field: 'reps' | 'weight', value: number | null) => {
    const newSets = [...workoutExercises];
    newSets[exerciseIndex].sets[setIndex][field] = value;
    
    // Always check for PB with the updated sets - this will update state with PB flags
    const updatedSet = newSets[exerciseIndex].sets[setIndex];
    await checkAndUpdatePB(exerciseIndex, setIndex, updatedSet.weight, updatedSet.reps, newSets);
    
    // checkAndUpdatePB will call setWorkoutExercises with PB flags, so state is already updated
    // The useEffect will automatically save to localStorage when workoutExercises changes
  };

  const completeSet = async (exerciseIndex: number, setIndex: number) => {
    const newSets = [...workoutExercises];
    const wasCompleted = newSets[exerciseIndex].sets[setIndex].completed;
    newSets[exerciseIndex].sets[setIndex].completed = !wasCompleted;
    
    const exercise = newSets[exerciseIndex].exercise;
    
    // For cardio or time-based exercises, directly update state (no PB tracking)
    if (isCardioExercise(exercise) || isTimeBasedExercise(exercise)) {
      setWorkoutExercises(newSets);
    } else {
      // CRITICAL: Re-check PBs with the updated completion status to ensure markers persist
      // This ensures PB flags are recalculated with the latest data including completion status
      const completedSet = newSets[exerciseIndex].sets[setIndex];
      await checkAndUpdatePB(exerciseIndex, setIndex, completedSet.weight, completedSet.reps, newSets);
      // checkAndUpdatePB will call setWorkoutExercises with PB flags AND completion status
    }

    if (newSets[exerciseIndex].sets[setIndex].completed) {
      const set = newSets[exerciseIndex].sets[setIndex];
      Analytics.exerciseSetLogged(exercise.name, {
        set_number: setIndex + 1,
        reps: set.reps,
        weight_kg: set.weight,
        exercise_category: exercise.category
      });
      toast.success(t('gymWorkout.toast.setCompleted'), { duration: 2000 });
    }
  };

  const removeExercise = (exerciseIndex: number) => {
    const removed = workoutExercises[exerciseIndex];
    const newWorkoutExercises = [...workoutExercises];
    newWorkoutExercises.splice(exerciseIndex, 1);
    setWorkoutExercises(newWorkoutExercises);

    // Cache is keyed by exercise name — only drop if no remaining exercise uses it.
    if (removed) {
      const nameKey = removed.exercise.name.trim().toLowerCase();
      const stillUsed = newWorkoutExercises.some(
        (ex) => ex.exercise.name.trim().toLowerCase() === nameKey,
      );
      if (!stillUsed) {
        setLastExerciseData((prev) => {
          const next = { ...prev };
          delete next[nameKey];
          return next;
        });
      }
    }
  };

  const importLastExerciseData = (exerciseName: string) => {
    const nameKey = exerciseName.trim().toLowerCase();
    const lastData = lastExerciseData[nameKey];
    if (!lastData) return;

    const updated = [...workoutExercises];
    
    // lastData is now directly the set_details array from the most recent workout
    const setDetails = Array.isArray(lastData) ? lastData : [lastData];
    
    if (setDetails.length === 0) {
      toast.error(t('gymWorkout.toast.noSetsToImport'), { duration: 2000 });
      return;
    }
    
    const exerciseIndex = updated.findIndex(
      (ex) => ex.exercise.name.trim().toLowerCase() === nameKey,
    );
    if (exerciseIndex < 0) return;

    // Create sets from all previous sets, preserving weight and reps
    updated[exerciseIndex].sets = setDetails.map((setDetail: any) => ({
      id: crypto.randomUUID(),
      reps: setDetail.reps || null,
      weight: setDetail.weight_kg ?? setDetail.weight ?? null,
      completed: false,
      isDropSet: !!(setDetail.isDropSet ?? setDetail.drop_set)
    }));

    setWorkoutExercises(updated);
    saveWorkoutSession();
    
    toast.success(t('gymWorkout.toast.importedSetsFromLastWorkout', { count: setDetails.length }), {
      duration: 2000
    });
  };

  const toggleDropSet = (exerciseIndex: number, setIndex: number) => {
    const updated = [...workoutExercises];
    const set = updated[exerciseIndex]?.sets[setIndex];
    if (!set) return;
    updated[exerciseIndex].sets[setIndex] = {
      ...set,
      isDropSet: !set.isDropSet,
    };
    setWorkoutExercises(updated);
    saveWorkoutSession();
  };

  const moveExercise = (exerciseIndex: number, direction: 'up' | 'down') => {
    const newWorkoutExercises = [...workoutExercises];
    const targetIndex = direction === 'up' ? exerciseIndex - 1 : exerciseIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= newWorkoutExercises.length) return;
    
    // Swap exercises
    [newWorkoutExercises[exerciseIndex], newWorkoutExercises[targetIndex]] = 
    [newWorkoutExercises[targetIndex], newWorkoutExercises[exerciseIndex]];
    
    setWorkoutExercises(newWorkoutExercises);
    setActiveRestTimer(prev => {
      if (!prev) return prev;
      if (prev.exerciseIndex === exerciseIndex) return { ...prev, exerciseIndex: targetIndex };
      if (prev.exerciseIndex === targetIndex) return { ...prev, exerciseIndex };
      return prev;
    });
    toast.success(direction === 'up' ? t('gymWorkout.toast.exerciseMovedUp') : t('gymWorkout.toast.exerciseMovedDown'), { duration: 2000 });
  };

  const checkAndUpdateGoals = async () => {
    if (!user) return;

    try {
      // Fetch active goals
      const { data: goals, error: goalsError } = await supabase
        .from('user_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (goalsError || !goals) return;

      // Check each exercise in the workout against goals
      for (const workoutExercise of workoutExercises) {
        const matchingGoal = goals.find(
          goal => goal.exercise_name?.toLowerCase() === workoutExercise.exercise.name.toLowerCase()
        );

        if (!matchingGoal) continue;

        // Find the best performance for this exercise in this workout
        let bestPerformance = 0;
        
        if (matchingGoal.goal_type === 'strength') {
          // Find max weight across completed sets
          const completedWeights = workoutExercise.sets
            .filter(set => set.completed && set.weight)
            .map(set => Number(set.weight));
          
          if (completedWeights.length > 0) {
            bestPerformance = Math.max(...completedWeights);
          }
        } else if (matchingGoal.goal_type === 'endurance') {
          // Find max reps across completed sets
          const completedReps = workoutExercise.sets
            .filter(set => set.completed && set.reps)
            .map(set => Number(set.reps));
          
          if (completedReps.length > 0) {
            bestPerformance = Math.max(...completedReps);
          }
        }

        // Check if this is a new personal best
        if (bestPerformance > 0) {
          const isNewPB = bestPerformance > matchingGoal.current_value;
          
          if (isNewPB) {
            // Update the goal with new personal best
            await supabase
              .from('user_goals')
              .update({ current_value: bestPerformance })
              .eq('id', matchingGoal.id)
              .eq('user_id', user.id);

            // Create milestone
            await supabase
              .from('milestones')
              .insert({
                user_id: user.id,
                goal_id: matchingGoal.id,
                milestone_type: 'personal_best',
                achievement_value: bestPerformance,
                achievement_unit: matchingGoal.target_unit
              });

            console.log(`New PB for ${workoutExercise.exercise.name}: ${bestPerformance} ${matchingGoal.target_unit}`);
          }
        }
      }
    } catch (error) {
      console.error('Error checking goals:', error);
      // Don't throw - we don't want to block workout saving if goal update fails
    }
  };

  const finishWorkout = async () => {
    if (workoutExercises.length === 0) {
      toast.error(t('gymWorkout.toast.addExerciseToSave'));
      return;
    }

    if (!user) {
      toast.error(t('gymWorkout.toast.loginRequiredToSave'));
      return;
    }

    // Check for incomplete sets (ignore cardio exercises)
    const incomplete = workoutExercises
      .map(exercise => {
        if (isCardioExercise(exercise.exercise)) {
          return { name: exercise.exercise.name, incompleteSets: 0, totalSets: 0 };
        }
        const incompleteSets = exercise.sets.filter(set => !set.completed).length;
        const totalSets = exercise.sets.length;
        return { name: exercise.exercise.name, incompleteSets, totalSets };
      })
      .filter(ex => ex.incompleteSets > 0);

    if (incomplete.length > 0) {
      setIncompleteExercises(incomplete);
      setShowIncompleteModal(true);
      return;
    }

    proceedWithWorkoutSave();
  };

  const proceedWithWorkoutSave = async () => {
    // Clear the saved session since workout is being saved
    localStorage.removeItem('activeWorkoutSession');

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      await signalWatchGymEnd();
    }
    
    setIsSaving(true);

    try {
      // Calculate workout duration in minutes
      const durationMinutes = workoutStartTime ? Math.round((new Date().getTime() - workoutStartTime.getTime()) / 1000 / 60) : 0;
      
      // Calculate total sets completed
      const completedSets = workoutExercises.reduce((total, exercise) => {
        return total + exercise.sets.filter(set => set.completed).length;
      }, 0);
      
      // Track workout completion
      Analytics.workoutCompleted(workoutTitle || 'Untitled Workout', {
        duration_minutes: durationMinutes,
        exercises_count: workoutExercises.length,
        sets_completed: completedSets
      });

      // Check and update goals based on workout performance
      await checkAndUpdateGoals();

      // Create workout record
      const { data: workout, error: workoutError } = await supabase
        .from('workouts')
        .insert({
          user_id: user.id,
          title: workoutTitle || "Gym Workout",
          description: `${workoutExercises.length} exercises, ${completedSets} sets completed`,
          workout_type: "strength",
          difficulty_level: "medium",
          duration_minutes: durationMinutes,
          completed_at: new Date().toISOString(),
          notes: workoutStartTime ? `Workout started at ${workoutStartTime.toLocaleTimeString()}` : undefined
        })
        .select()
        .single();

      if (workoutError) {
        console.error('Error saving workout:', workoutError);
        toast.error(t('gymWorkout.toast.saveFailed'));
        return;
      }

      // Save exercises for this workout - save each set individually
      const exercisesToSave = [];
      
      for (const workoutExercise of workoutExercises) {
        // Cardio: save as duration-only entry
        if (isCardioExercise(workoutExercise.exercise)) {
          exercisesToSave.push({
            workout_id: workout.id,
            name: workoutExercise.exercise.name,
            muscle_groups: workoutExercise.exercise.muscle_groups,
            equipment: workoutExercise.exercise.equipment || null,
            duration_seconds: (workoutExercise.durationMinutes || 0) * 60,
            sets: null,
            reps: null,
            weight_kg: null,
            category: 'Cardio',
            notes: workoutExercise.notes || null
          });
          continue;
        }

        // Time-based exercises (like Plank): save duration in seconds, no weight
        if (isTimeBasedExercise(workoutExercise.exercise)) {
          const completedSetsForExercise = workoutExercise.sets.filter(set => set.completed);
          
          if (completedSetsForExercise.length > 0) {
            for (let i = 0; i < completedSetsForExercise.length; i++) {
              const set = completedSetsForExercise[i];
              exercisesToSave.push({
                workout_id: workout.id,
                name: workoutExercise.exercise.name,
                muscle_groups: workoutExercise.exercise.muscle_groups,
                equipment: workoutExercise.exercise.equipment || null,
                sets: i + 1,
                reps: null,
                weight_kg: null,
                duration_seconds: set.reps || 0, // reps field stores duration in seconds for time-based
                category: 'Strength',
                notes: i === 0 ? workoutExercise.notes || null : null
              });
            }
          }
          continue;
        }

        const completedSetsForExercise = workoutExercise.sets.filter(set => set.completed);
        
        if (completedSetsForExercise.length > 0) {
          // Build set_details array with all set information including rest times
          const setDetails = completedSetsForExercise.map((set, idx) => ({
            set_number: idx + 1,
            reps: set.reps,
            weight_kg: set.weight,
            completed: set.completed,
            rest_seconds_taken: set.rest_seconds_taken || null,
            drop_set: set.isDropSet || false
          }));

          // Create ONE exercise entry per exercise with all sets in set_details
          // This prevents multiple exercise entries for the same exercise in one workout
          exercisesToSave.push({
            workout_id: workout.id,
            name: workoutExercise.exercise.name,
            muscle_groups: workoutExercise.exercise.muscle_groups,
            equipment: workoutExercise.exercise.equipment || null,
            sets: completedSetsForExercise.length, // Total number of completed sets
            reps: completedSetsForExercise[0]?.reps || null, // First set's reps (for backwards compatibility)
            weight_kg: completedSetsForExercise[0]?.weight || null, // First set's weight (for backwards compatibility)
            set_details: setDetails, // All sets with full details
            notes: workoutExercise.notes || null
          });
        } else {
          // If no sets completed, still save the exercise with 0 sets
          exercisesToSave.push({
            workout_id: workout.id,
            name: workoutExercise.exercise.name,
            muscle_groups: workoutExercise.exercise.muscle_groups,
            equipment: workoutExercise.exercise.equipment || null,
            sets: 0,
            reps: null,
            weight_kg: null,
            notes: workoutExercise.notes || null
          });
        }
      }

      const { error: exercisesError } = await supabase
        .from('exercises')
        .insert(exercisesToSave);

      if (exercisesError) {
        console.error('Error saving exercises:', exercisesError);
        toast.error(t('gymWorkout.toast.savePartialFailure'));
      } else {
        // Clear the saved session since workout was successfully saved
        localStorage.removeItem('activeWorkoutSession');
        
        // Store workout data for the completion modal
        setCompletedWorkoutData({
          title: workoutTitle || "Gym Workout",
          duration: durationMinutes,
          completedSets,
          workoutId: workout.id,
          workoutDetails: {
            exercises: workoutExercises,
            startTime: workoutStartTime,
            endTime: new Date()
          }
        });
        setShowCompletionModal(true);
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error(t('common.unexpectedError'));
      navigate("/workouts");
    } finally {
      setIsSaving(false);
    }
  };

  const cancelWorkout = () => {
    // Clear the active workout session
    localStorage.removeItem('activeWorkoutSession');
    // Track workout abandonment
    Analytics.workoutAbandoned(workoutTitle || 'Untitled Workout', {
      exercises_count: workoutExercises.length,
      duration_minutes: workoutStartTime ? Math.round((new Date().getTime() - workoutStartTime.getTime()) / 1000 / 60) : 0
    });
    // Reset all workout state
    setWorkoutExercises([]);
    setWorkoutTitle("");
    setWorkoutStarted(false);
    setWorkoutStartTime(null);
    setShowCancelDialog(false);
    toast.info(t('gymWorkout.toast.workoutCancelled'));
    navigate("/workouts");
  };

  return (
    <div className="w-full p-4 sm:p-6 space-y-4 sm:space-y-6 sm:max-w-4xl sm:mx-auto pb-24">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={() => navigate("/workouts")} className="p-2 flex-shrink-0">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base sm:text-xl font-normal truncate">{t('gymWorkout.pageTitle')}</h1>
          {workoutStarted && workoutStartTime && (
            <p className="text-xs sm:text-sm text-muted-foreground">
              {t('gymWorkout.startedAt', { time: workoutStartTime.toLocaleTimeString() })}
            </p>
          )}
        </div>
        {/* Chat with Arnold button */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/chat", { state: { fromWorkout: true } })}
          className="border-0 text-primary hover:bg-primary/10 text-xs sm:text-sm px-2 sm:px-3 flex-shrink-0"
          title={t('gymWorkout.chatWithCoach')}
        >
          <span className="hidden sm:inline">💬 {t('gymWorkout.coachButton')}</span>
          <span className="sm:hidden">💬</span>
        </Button>
      </div>

      {/* Workout Title */}
      <Card>
        <CardContent className="p-4">
          <Label htmlFor="workout-title">{t('gymWorkout.labels.workoutName')}</Label>
          <Input
            id="workout-title"
            placeholder={t('gymWorkout.labels.workoutNamePlaceholder')}
            value={workoutTitle}
            onChange={(e) => setWorkoutTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }}
            className="mt-2 w-full"
          />
        </CardContent>
      </Card>

      {/* Workout Timer */}
      <WorkoutTimer isActive={workoutStarted} startTime={workoutStartTime || undefined} />

      {/* Search/Select Exercise - overflow-visible + high z-index so dropdowns appear above workout cards */}
      <Card className="w-full min-w-full overflow-visible relative z-20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm sm:text-lg">
            <Search className="h-5 w-5" />
            {t('gymWorkout.searchCard.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 w-full min-w-full overflow-visible">
          {/* Import and Custom Exercise Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 justify-end">
            <Button
              variant="ghost"
              onClick={() => setShowProgramImportModal(true)}
              className="border-0 hover:bg-accent/50 text-xs sm:text-sm px-3 sm:px-4"
            >
              <Download className="h-3 sm:h-4 w-3 sm:w-4 mr-1 sm:mr-2" />
              {t('gymWorkout.actions.importFromProgram')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowCustomExerciseModal(true)}
              className="border-0 hover:bg-accent/50 text-xs sm:text-sm px-3 sm:px-4"
            >
              <Dumbbell className="h-3 sm:h-4 w-3 sm:w-4 mr-1 sm:mr-2" />
              {t('gymWorkout.actions.createCustomExercise')}
            </Button>
          </div>
          {/* Category Filter - z-[9999] so dropdown appears above Search bar and exercise cards */}
          <div className={`relative category-dropdown-container w-full min-w-full ${showCategoryDropdown ? 'z-[9999]' : ''}`}>
            <Label className="text-sm font-medium">{t('gymWorkout.labels.filterByMuscleGroup')}</Label>
            <div className="relative mt-2 w-full min-w-full">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full min-w-full h-10 px-3 py-2 text-left bg-background border-0 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-between"
                style={{ width: '100%', minWidth: '100%' }}
              >
                <span className="text-sm">{selectedCategory}</span>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </button>
              
               {showCategoryDropdown && (
                 <div className="absolute z-[9999] w-full mt-1 bg-background rounded-lg shadow-lg max-h-60 overflow-y-auto gym-scroll-no-highlight">
                  {categories.map((category) => (
                    <div
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowCategoryDropdown(false);
                      }}
                      className="gym-dropdown-item p-3 hover:bg-accent cursor-pointer"
                    >
                      <div className="font-medium text-sm">{category}</div>
                      {category !== "All" && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {EXERCISES.filter(ex => ex.category === category).length} exercises
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Search Input - z-[9998] when dropdown open so it appears above workout cards, below category dropdown */}
          <div className={`relative exercise-search-container w-full min-w-full ${showExerciseList ? 'z-[9998]' : ''}`}>
            <Label className="text-sm font-medium">{t('gymWorkout.labels.searchExercises')}</Label>
            <div className="relative mt-2 w-full min-w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('gymWorkout.labels.searchPlaceholder')}
                value={exerciseSearch}
                onChange={(e) => {
                  setExerciseSearch(e.target.value);
                  setShowExerciseList(e.target.value.length > 0 || selectedCategory !== "All");
                }}
                onFocus={(e) => {
                  setShowExerciseList(exerciseSearch.length > 0 || selectedCategory !== "All");
                  // Scroll into view to prevent keyboard overlap
                  setTimeout(() => {
                    e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 300);
                }}
                className="pl-10 w-full min-w-full"
                style={{ width: '100%', minWidth: '100%' }}
              />
            </div>
            
           {/* Exercise List Dropdown - z-[9999] so it appears above workout cards and search bar */}
           {(showExerciseList && filteredExercises.length > 0) && (
             <div className="absolute z-[9999] w-full mt-1 bg-background rounded-lg shadow-lg max-h-60 overflow-y-auto gym-scroll-no-highlight">
              {filteredExercises.map((exercise) => (
                <div
                  key={exercise.id}
                  onClick={() => addExercise(exercise)}
                  className="gym-dropdown-item p-3 hover:bg-accent cursor-pointer group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{exercise.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {exercise.muscle_groups.join(", ")} • {exercise.equipment}
                        {'is_custom' in exercise && exercise.is_custom && (
                          <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                            {t('gymWorkout.badges.custom')}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {exercise.category}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedExerciseForInfo(exercise.name);
                        setShowInfoModal(true);
                      }}
                      className="flex-shrink-0 p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors border-0"
                      title={t('gymWorkout.tooltips.viewExerciseDetails')}
                    >
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* No results */}
          {showExerciseList && (exerciseSearch || selectedCategory !== "All") && filteredExercises.length === 0 && (
            <div className="absolute z-[9999] w-full mt-1 bg-background rounded-lg shadow-lg p-3 text-center text-muted-foreground">
              {t('gymWorkout.empty.noExercisesForFilters')}
            </div>
          )}
          
          {/* Show exercises button when category is selected but no search */}
          {selectedCategory !== "All" && !exerciseSearch && !showExerciseList && (
            <Button
              variant="ghost"
              onClick={() => setShowExerciseList(true)}
              className="w-full mt-2 border-0"
            >
              Show {selectedCategory} Exercises ({EXERCISES.filter(ex => ex.category === selectedCategory).length})
            </Button>
          )}
          </div>
        </CardContent>
      </Card>

      {/* Workout Exercises - z-10 so they stay below search dropdowns */}
      {workoutExercises.map((workoutExercise, exerciseIndex) => (
        <Card key={exerciseIndex} className="bg-gradient-card relative z-10">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              {/* Left: name and meta */}
              <div className="flex items-start gap-3 min-w-0">
                {/* Exercise order controls (hide on mobile) */}
                <div className="flex flex-col gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveExercise(exerciseIndex, 'up')}
                    disabled={exerciseIndex === 0}
                    className="h-6 w-6 p-0"
                    title={t('gymWorkout.tooltips.moveExerciseUp')}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveExercise(exerciseIndex, 'down')}
                    disabled={exerciseIndex === workoutExercises.length - 1}
                    className="h-6 w-6 p-0"
                    title={t('gymWorkout.tooltips.moveExerciseDown')}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>
                <div className="block text-sm text-muted-foreground font-medium">
                  #{exerciseIndex + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base sm:text-lg font-semibold leading-snug">
                    {workoutExercise.exercise.name}
                  </CardTitle>
                  <div className="hidden sm:flex gap-1 mt-1 flex-wrap">
                    {workoutExercise.exercise.muscle_groups.map((muscle) => (
                      <Badge key={muscle} variant="secondary" className="text-xs">
                        {muscle}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right: actions - stacks below on mobile */}
              <div className="flex w-full sm:w-auto justify-end gap-1 sm:gap-2 flex-shrink-0">
                {!isCardioExercise(workoutExercise.exercise) && (
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => importLastExerciseData(workoutExercise.exercise.name)}
                    className="h-11 w-11 sm:h-10 sm:w-10 touch-manipulation border-primary/40 hover:bg-primary/20"
                    title={t('gymWorkout.tooltips.importRecentData')}
                    aria-label={t('gymWorkout.aria.importRecentData')}
                    disabled={!lastExerciseData[workoutExercise.exercise.name.trim().toLowerCase()]}
                  >
                    <Download className="h-5 w-5 text-primary" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setSelectedExerciseForInfo(workoutExercise.exercise.name);
                    setShowInfoModal(true);
                  }}
                  className="h-11 w-11 sm:h-10 sm:w-10 touch-manipulation"
                  title={t('gymWorkout.tooltips.exerciseInfo')}
                  aria-label={t('gymWorkout.aria.exerciseInfo')}
                >
                  <Info className="h-5 w-5 text-primary" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    Analytics.restTimerStarted(workoutExercises[exerciseIndex].exercise.name);
                    setActiveRestTimer({ exerciseIndex, setIndex: 0 });
                  }}
                  className="h-11 w-11 sm:h-10 sm:w-10 touch-manipulation"
                  title={t('gymWorkout.tooltips.restTimer')}
                  aria-label={t('gymWorkout.aria.startRestTimer')}
                >
                  <Timer className="h-5 w-5 text-primary" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSelectedExerciseForHistory(workoutExercise.exercise.name);
                    setShowHistoryModal(true);
                  }}
                  className="h-11 w-11 sm:h-10 sm:w-10 touch-manipulation text-primary"
                  title={t('gymWorkout.tooltips.viewExerciseHistory')}
                  aria-label={t('gymWorkout.aria.viewExerciseHistory')}
                >
                  <History className="h-5 w-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeExercise(exerciseIndex)}
                  className="h-11 w-11 sm:h-10 sm:w-10 touch-manipulation text-destructive"
                  title={t('gymWorkout.tooltips.removeExercise')}
                  aria-label={t('gymWorkout.aria.removeExercise')}
                >
                  <Minus className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {isCardioExercise(workoutExercise.exercise) ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2 flex-1">
                    <Label htmlFor={`duration-${exerciseIndex}`}>{t('gymWorkout.labels.durationMinutes')}</Label>
                    <Input
                      id={`duration-${exerciseIndex}`}
                      type="text"
                      inputMode="numeric"
                      placeholder="30"
                      value={workoutExercise.durationMinutes || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          const minutes = value ? parseInt(value) : 0;
                          const updated = [...workoutExercises];
                          updated[exerciseIndex].durationMinutes = minutes;
                          setWorkoutExercises(updated);
                          saveWorkoutSession();
                        }
                      }}
                      onFocus={handleInputFocus}
                      onKeyDown={handleInputKeyDown}
                      className="h-9 w-24 text-center"
                    />
                  </div>
                  <div className="ml-4">
                    <Button
                      size="default"
                      variant={workoutExercise.sets.length > 0 && workoutExercise.sets[0]?.completed ? "default" : "outline"}
                      onClick={() => {
                        const updated = [...workoutExercises];
                        if (updated[exerciseIndex].sets.length === 0) {
                          // Add a dummy set for cardio completion tracking
                          updated[exerciseIndex].sets = [{ 
                            id: crypto.randomUUID(), 
                            reps: null, 
                            weight: null, 
                            completed: false 
                          }];
                        }
                        updated[exerciseIndex].sets[0].completed = !updated[exerciseIndex].sets[0].completed;
                        setWorkoutExercises(updated);
                        saveWorkoutSession();
                        
                        if (updated[exerciseIndex].sets[0].completed) {
                          toast.success(t('gymWorkout.toast.cardioCompleted'), { duration: 2000 });
                        }
                      }}
                      className={workoutExercise.sets.length > 0 && workoutExercise.sets[0]?.completed ? 
                        "bg-green-600 hover:bg-green-700 text-white" : 
                        "border-primary text-primary hover:bg-primary hover:text-white"
                      }
                    >
                      {workoutExercise.sets.length > 0 && workoutExercise.sets[0]?.completed ? (
                        <>
                          <Check className="h-4 w-4 mr-2" />
                          {t('gymWorkout.actions.completed')}
                        </>
                      ) : (
                        <>
                          <Dumbbell className="h-4 w-4 mr-2" />
                          {t('gymWorkout.actions.logExercise')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            ) : isTimeBasedExercise(workoutExercise.exercise) ? (
              <>
                {/* Time-based Sets Header */}
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-muted-foreground px-2">
                  <div className="col-span-2">{t('gymWorkout.sets.columnSet')}</div>
                  <div className="col-span-4">{t('gymWorkout.sets.columnDurationSec')}</div>
                  <div className="col-span-3">{t('gymWorkout.sets.columnDone')}</div>
                  <div className="col-span-3"></div>
                </div>

                {/* Time-based Sets */}
                {workoutExercise.sets.map((set, setIndex) => (
                  <div key={set.id} className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-2 text-center font-medium">
                        {setIndex + 1}
                      </div>
                      <div className="col-span-4">
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="30"
                          value={set.reps || ""}
                          onFocus={handleInputFocus}
                          onKeyDown={handleInputKeyDown}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d+$/.test(value)) {
                              updateSet(exerciseIndex, setIndex, 'reps', value ? parseInt(value) : null);
                            }
                          }}
                          className="h-9 text-center"
                        />
                      </div>
                      <div className="col-span-3 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => completeSet(exerciseIndex, setIndex)}
                          className={`h-8 w-9 p-0 border ${
                            set.completed
                              ? "bg-gradient-primary text-black border-transparent hover:opacity-90"
                              : "border-primary/40 text-primary bg-transparent hover:bg-primary/10"
                          }`}
                          title={t('gymWorkout.sets.markSetDone')}
                          aria-label={t('gymWorkout.sets.markSetDone')}
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="col-span-3">
                        {workoutExercise.sets.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeSet(exerciseIndex, setIndex)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Set Buttons for time-based */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => addSet(exerciseIndex, true)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('gymWorkout.actions.addSameSet')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => addSet(exerciseIndex, false)}
                    className="bg-gradient-primary text-black hover:opacity-90"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('gymWorkout.actions.addSet')}
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Sets Header */}
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-muted-foreground px-2">
                  <div className="col-span-1" aria-hidden />
                  <div className="col-span-2 text-center">{t('gymWorkout.sets.columnSet')}</div>
                  <div className="col-span-3">{t('gymWorkout.sets.columnReps')}</div>
                  <div className="col-span-3">{`Weight (${weightLabel})`}</div>
                  <div className="col-span-2 text-center">{t('gymWorkout.sets.columnDone')}</div>
                  <div className="col-span-1" aria-hidden />
                </div>

                {/* Sets */}
                {workoutExercise.sets.map((set, setIndex) => (
                  <div key={set.id} className="space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-1 flex justify-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-foreground"
                              aria-label={t("gymWorkout.aria.setRowMenu")}
                            >
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="min-w-[10rem]">
                            <DropdownMenuCheckboxItem
                              checked={!!set.isDropSet}
                              onCheckedChange={(checked) => {
                                if (checked !== !!set.isDropSet) {
                                  toggleDropSet(exerciseIndex, setIndex);
                                }
                              }}
                            >
                              {t("gymWorkout.sets.dropSet")}
                            </DropdownMenuCheckboxItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="col-span-2 text-center font-medium inline-flex items-center justify-center gap-0.5 flex-wrap">
                        <span>{setIndex + 1}</span>
                        {set.isDropSet && (
                          <span
                            className="text-sm font-bold leading-none bg-gradient-primary bg-clip-text text-transparent"
                            title={t("gymWorkout.tooltips.dropSet")}
                            aria-hidden
                          >
                            *
                          </span>
                        )}
                      </div>
                      <div className="col-span-3 relative">
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={repsInputs[set.id] ?? (set.reps ?? "")}
                          onFocus={handleInputFocus}
                          onKeyDown={handleInputKeyDown}
                          onChange={(e) => {
                            const value = e.target.value;
                            setRepsInputs(prev => ({ ...prev, [set.id]: value }));
                            // Only update display value, don't trigger PB check yet
                            // Preserve PB flags when updating display value
                            if (value === '' || /^\d+$/.test(value)) {
                              const newSets = [...workoutExercises];
                              const currentSet = newSets[exerciseIndex].sets[setIndex];
                              newSets[exerciseIndex].sets[setIndex] = {
                                ...currentSet,
                                reps: value ? parseInt(value) : null
                              };
                              setWorkoutExercises(newSets);
                            }
                          }}
                          onBlur={() => {
                            const value = repsInputs[set.id] ?? '';
                            const num = value === '' ? null : parseInt(value);
                            updateSet(exerciseIndex, setIndex, 'reps', isNaN(Number(num)) ? null : num);
                            setRepsInputs(prev => ({ ...prev, [set.id]: num == null || isNaN(Number(num)) ? '' : String(num) }));
                          }}
                          className="h-9 text-center"
                        />
                        {set.isRepsPB && (
                          <Badge 
                            className="absolute -top-2 -right-2 bg-gradient-to-r from-[#f5b52a] to-[#f5c84a] text-black text-[10px] font-bold px-2 py-0.5 shadow-lg animate-pulse z-20 pointer-events-none"
                            style={{ 
                              boxShadow: '0 0 15px rgba(245, 181, 42, 0.6)',
                              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                              transform: 'scale(1.1)'
                            }}
                          >
                            {t('gymWorkout.badges.pbShort')}
                          </Badge>
                        )}
                      </div>
                      <div className="col-span-3 relative">
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="0"
                          value={weightInputs[set.id] ?? (set.weight != null ? String(Math.round(kgToDisplayWeight(set.weight, units) * 10) / 10) : "")}
                          onFocus={handleInputFocus}
                          onKeyDown={handleInputKeyDown}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setWeightInputs(prev => ({ ...prev, [set.id]: raw }));
                            // Only update display value, don't trigger PB check yet
                            // Preserve PB flags when updating display value
                            const normalized = raw.replace(',', '.');
                            if (/^\d*([.,]\d*)?$/.test(raw)) {
                              const num = parseFloat(normalized);
                              const kg = raw === '' || isNaN(num) ? null : displayWeightToKg(num, units);
                              const newSets = [...workoutExercises];
                              const currentSet = newSets[exerciseIndex].sets[setIndex];
                              newSets[exerciseIndex].sets[setIndex] = {
                                ...currentSet,
                                weight: kg
                              };
                              setWorkoutExercises(newSets);
                            }
                          }}
                          onBlur={() => {
                            const raw = weightInputs[set.id] ?? '';
                            const normalized = raw.replace(',', '.');
                            const num = normalized === '' || normalized === '.' ? null : parseFloat(normalized);
                            const kg = num == null || isNaN(num) ? null : displayWeightToKg(num, units);
                            updateSet(exerciseIndex, setIndex, 'weight', kg);
                            setWeightInputs(prev => ({
                              ...prev,
                              [set.id]: kg == null
                                ? ''
                                : String(Math.round(kgToDisplayWeight(kg, units) * 10) / 10),
                            }));
                          }}
                          className="h-9 text-center"
                        />
                        {set.isWeightPB && (
                          <Badge 
                            className="absolute -top-2 -right-2 bg-gradient-to-r from-[#f5b52a] to-[#f5c84a] text-black text-[10px] font-bold px-2 py-0.5 shadow-lg animate-pulse z-20 pointer-events-none"
                            style={{ 
                              boxShadow: '0 0 15px rgba(245, 181, 42, 0.6)',
                              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                              transform: 'scale(1.1)'
                            }}
                          >
                            {t('gymWorkout.badges.pbShort')}
                          </Badge>
                        )}
                      </div>
                      <div className="col-span-2 text-center relative flex flex-row items-center justify-center gap-1 min-w-0 py-0.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => completeSet(exerciseIndex, setIndex)}
                          className={`h-8 w-9 shrink-0 p-0 border ${
                            set.completed
                              ? "bg-gradient-primary text-black border-transparent hover:opacity-90"
                              : "border-primary/40 text-primary bg-transparent hover:bg-primary/10"
                          }`}
                          title={t('gymWorkout.sets.markSetDone')}
                          aria-label={t('gymWorkout.sets.markSetDone')}
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        {set.isTotalWeightPB && (
                          <Badge 
                            className="absolute -top-2 -right-2 bg-gradient-to-r from-[#f5b52a] to-[#f5c84a] text-black text-[10px] font-bold px-2 py-0.5 shadow-lg animate-pulse z-20 pointer-events-none"
                            style={{ 
                              boxShadow: '0 0 15px rgba(245, 181, 42, 0.6)',
                              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                              transform: 'scale(1.1)'
                            }}
                          >
                            {t('gymWorkout.badges.pbShort')}
                          </Badge>
                        )}
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {workoutExercise.sets.length > 1 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeSet(exerciseIndex, setIndex)}
                            className="h-8 w-8 shrink-0 p-0 text-destructive hover:text-destructive"
                            aria-label={t("gymWorkout.sets.removeSet")}
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add Set Buttons */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => addSet(exerciseIndex, true)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('gymWorkout.actions.addSameSet')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => addSet(exerciseIndex, false)}
                    className="bg-gradient-primary text-black hover:opacity-90"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {t('gymWorkout.actions.addSet')}
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Empty State - z-10 so it stays below search dropdowns (was overlapping exercise dropdown) */}
      {workoutExercises.length === 0 && (
        <Card className="text-center py-12 relative z-10">
          <CardContent>
            <div className="text-muted-foreground">
              <div className="text-lg font-medium mb-2">{t('gymWorkout.empty.readyTitle')}</div>
              <div className="text-sm">{t('gymWorkout.empty.readyHint')}</div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Custom Exercise Modal */}
      <AddCustomExerciseModal
        isOpen={showCustomExerciseModal}
        onClose={() => setShowCustomExerciseModal(false)}
        onExerciseAdded={handleCustomExerciseAdded}
      />

      {/* Exercise History Modal */}
      <ExerciseHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        exerciseName={selectedExerciseForHistory}
      />

      {/* Exercise Info Modal */}
      <ExerciseInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        exerciseName={selectedExerciseForInfo}
      />

      <WorkoutCompletionModal
        isOpen={showCompletionModal}
        onClose={() => setShowCompletionModal(false)}
        onAnalyzeNow={handleAnalyzeWorkout}
        onAnalyzeLater={handleAnalyzeLater}
        onShare={handleShareWorkout}
        workoutTitle={completedWorkoutData?.title || ""}
        duration={completedWorkoutData?.duration || 0}
        completedSets={completedWorkoutData?.completedSets || 0}
      />

      <ShareWorkoutModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShowCompletionModal(true);
        }}
        workout={buildShareableWorkout()}
      />

      <IncompleteWorkoutModal
        isOpen={showIncompleteModal}
        onClose={() => setShowIncompleteModal(false)}
        onContinueAnyway={() => {
          setShowIncompleteModal(false);
          proceedWithWorkoutSave();
        }}
        onGoBack={() => setShowIncompleteModal(false)}
        incompleteExercises={incompleteExercises}
      />

      {/* Workout Program Import Modal */}
      <WorkoutProgramImportModal
        isOpen={showProgramImportModal}
        onClose={() => setShowProgramImportModal(false)}
        onImport={handleImportFromProgram}
      />

      {/* Rest Timer Modal */}
      {activeRestTimer !== null && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm">
            <RestTimer
              defaultRestSeconds={90}
              onClose={() => setActiveRestTimer(null)}
              onRestComplete={(actualRestSeconds) => {
                // Save the rest time to the corresponding set
                if (activeRestTimer) {
                  const { exerciseIndex, setIndex } = activeRestTimer;
                  const updated = [...workoutExercises];
                  if (updated[exerciseIndex]?.sets[setIndex]) {
                    updated[exerciseIndex].sets[setIndex].rest_seconds_taken = actualRestSeconds;
                    setWorkoutExercises(updated);
                    saveWorkoutSession();
                  }
                }
                setActiveRestTimer(null);
              }}
            />
          </div>
        </div>
      )}

      {/* Finish Workout Button */}
      {workoutStarted && (
        <>
          <div className="mt-6 space-y-3">
            <Button 
              onClick={finishWorkout} 
              disabled={isSaving} 
              className="w-full h-14 text-lg font-semibold bg-gradient-primary text-black shadow-glow hover:shadow-lg transition-all"
            >
              <Check className="h-5 w-5 mr-2" />
              {isSaving ? t('gymWorkout.actions.savingWorkout') : t('gymWorkout.actions.finishWorkout')}
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => setShowCancelDialog(true)} 
              disabled={isSaving} 
              className="w-full h-12 text-base font-semibold border-0 text-destructive hover:bg-destructive/10"
            >
              <X className="h-4 w-4 mr-2" />
              {t('gymWorkout.actions.cancelWorkout')}
            </Button>
          </div>
          {/* Spacer below button to clear bottom tab and safe area */}
          <div aria-hidden className="h-28 safe-bottom" />
        </>
      )}

      {/* Cancel Workout Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gymWorkout.cancelDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('gymWorkout.cancelDialog.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('gymWorkout.cancelDialog.keepWorkout')}</AlertDialogCancel>
            <AlertDialogAction onClick={cancelWorkout} className="bg-destructive hover:bg-destructive/90">
              {t('gymWorkout.cancelDialog.confirmCancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};